/**
 * reportDataAdapter 现成接口拼装实现（V3.0.7）：
 * - 成组/摘要：listTrackers(unread_only, last_triggered DESC, 翻页全量) 单一数据源
 *   —— goals = 非空 goal_id 去重分组（每组取 last_triggered 最大 tracker 的 last_trigger_title
 *   作描述行），trackers = 无 goal_id 项（接口序）；market 由消费方 marketUnread/reportDate 判定
 * - 恒等：unread_count = goals+trackers+(market?1:0) 单点派生（fetchSummary 内一处）
 * - bar_state：本地 watermark 派生（reportSummaryWatermark；GAP-1 服务端方案就绪后下线）
 * - goal 详情：getGoal?enrich_trackers=true 映射（plan 卡 = goal 名下 USER tracker，
 *   与非汇报态 buildGoalDetailPlanCards 同语义）
 * - Ask Ed direct_context 原料：tracker 页从 records title/description 拼、goal 页从 goal 详情拼
 * - 已读落定：updateTracker(unread_count=0)（tracker 级；goal 级 = 组内逐个）
 */
import {
  TrackerActorEnum,
  type Goal,
  type Tracker,
  type TrackerClient,
  type TrackerPagination,
  type TrackerRecord,
  type TrackerRecordPagination,
} from "@features/api/TrackerApi";
import type {
  ReportAdapterDeps,
  ReportDataAdapter,
  ReportGoalDetail,
  ReportGoalPlan,
  ReportGroup,
  ReportSettleDep,
  ReportTaskItem,
} from "./reportDataAdapter";
import {
  hasEnteredReportToday,
  readReportSummaryWatermark,
  writeReportSummaryWatermark,
} from "./reportSummaryWatermark";
import { resolveTrackerPlanCardStatus } from "@utils/trackerStatus";

const LIST_PAGE_SIZE = 100;
/** 翻页安全上限（100/页 × 50 页 = 5000，超出按异常环兜底截断） */
const MAX_LIST_PAGES = 50;
const DAY_MS = 24 * 60 * 60 * 1000;
const MONTH_MS = 30.44 * DAY_MS;

function toLocalIsoDate(d = new Date()): string {
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** 同 useTrackerListInfinite：兼容 data 直挂/包裹两种返回形态 */
function normalizePagination<
  T extends { rows?: unknown[]; next_token?: string },
>(payload: unknown): T {
  if (!payload || typeof payload !== "object") {
    return {} as T;
  }
  const direct = payload as T;
  if (Array.isArray(direct.rows)) {
    return direct;
  }
  const wrapped = payload as { data?: T };
  if (wrapped.data && Array.isArray(wrapped.data.rows)) {
    return wrapped.data;
  }
  return direct;
}

function epochMsToIso(ms: number | undefined): string | undefined {
  return typeof ms === "number" && Number.isFinite(ms) && ms > 0
    ? new Date(ms).toISOString()
    : undefined;
}

async function listAllUnreadTrackers(
  deps: Pick<ReportSettleDep, "trackerApi" | "ownerId">,
): Promise<Tracker[]> {
  const rows: Tracker[] = [];
  let nextToken: string | undefined;
  for (let page = 0; page < MAX_LIST_PAGES; page += 1) {
    const res = await deps.trackerApi.trackers.listTrackers({
      owner_id: deps.ownerId,
      unread_only: true,
      sort_field: "last_triggered",
      sort_order: "DESC",
      limit: LIST_PAGE_SIZE,
      ...(nextToken ? { next_token: nextToken } : {}),
    });
    const data = normalizePagination<TrackerPagination>(res?.data ?? res);
    rows.push(...(data.rows ?? []));
    nextToken =
      data.next_token && data.next_token.length > 0
        ? data.next_token
        : undefined;
    if (!nextToken) {
      break;
    }
  }
  return rows;
}

function maxByLastTriggered(rows: Tracker[]): Tracker {
  return rows.reduce((latest, current) =>
    (current.last_triggered ?? 0) >= (latest.last_triggered ?? 0)
      ? current
      : latest,
  );
}

function toTaskItem(
  kind: "goal" | "tracker",
  taskId: string,
  latest: Tracker,
): ReportTaskItem {
  return {
    task_id: taskId,
    kind,
    latest_unread_title: latest.last_trigger_title ?? latest.title ?? "",
    last_signal_at: epochMsToIso(latest.last_triggered),
    unread_signal_ids: [],
  };
}

/** goals 组内按 last_triggered DESC（组代表=组内最大）；goals 间按组代表 last_triggered DESC */
function buildGroup(
  rows: Tracker[],
  date: string,
  deps: Pick<ReportAdapterDeps, "marketUnread" | "reportDate">,
): ReportGroup {
  const goalGroups = new Map<string, Tracker[]>();
  const standalone: Tracker[] = [];
  for (const row of rows) {
    if (!row.tracker_id) {
      continue;
    }
    if (row.goal_id && row.goal_id.length > 0) {
      const members = goalGroups.get(row.goal_id) ?? [];
      members.push(row);
      goalGroups.set(row.goal_id, members);
    } else {
      standalone.push(row);
    }
  }

  const goals: ReportTaskItem[] = [...goalGroups.entries()]
    .map(([goalId, members]) => {
      const latest = maxByLastTriggered(members);
      return {
        ...toTaskItem("goal", goalId, latest),
        tracker_ids: members
          .map((m) => m.tracker_id)
          .filter((id): id is string => Boolean(id)),
      };
    })
    .sort(
      (a, b) =>
        (Date.parse(b.last_signal_at ?? "") || 0) -
        (Date.parse(a.last_signal_at ?? "") || 0),
    );

  // PRD-A A.1 独立计数（评审批 P1）：trackers 块=全部未读 tracker（含挂 goal 的），
  // 与 goals 块各自去重、互不互斥——1 goal 挂 3 未读 tracker = goal 页 1 + tracker 页 3。
  // standalone 分桶仅保留给调试语义，不再决定 trackers 成员。
  void standalone;
  const trackers: ReportTaskItem[] = rows
    .filter((row) => Boolean(row.tracker_id))
    .map((row) => toTaskItem("tracker", row.tracker_id!, row));

  return {
    date,
    market_summary:
      deps.marketUnread && deps.reportDate
        ? {
            date: deps.reportDate,
            title: "Daily market summary",
            included: true,
          }
        : null,
    goals,
    trackers,
  };
}

/** desk 同映射（resolveGoalDetailPlanStatus）：未来 start→not_started / ACTIVE|STOP→in_progress /
 *  COMPLETED→completed / DELETE→cancelled */
function toReportPlanStatus(
  tracker: Tracker,
  nowMs: number,
): ReportGoalPlan["status"] {
  if (
    typeof tracker.start === "number" &&
    Number.isFinite(tracker.start) &&
    tracker.start > nowMs
  ) {
    return "not_started";
  }
  return resolveTrackerPlanCardStatus(tracker.status);
}

/**
 * goal.review 解析（口径＝非汇报态 GoalDetailPage :157 逐字）：
 * JSON 串 `{"title","summary"}` 拆字段；解析失败整串作 title；空串/缺省 → null（无 review）。
 */
function parseGoalReview(
  review?: string | null,
): { title?: string; summary?: string } | null {
  const trimmed = review?.trim();
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed) as { title?: string; summary?: string };
  } catch {
    return { title: trimmed };
  }
}

function mapGoalToReportDetail(
  goal: Goal,
  nowMs = Date.now(),
): ReportGoalDetail {
  const current = goal.current_value ?? 0;
  const target = goal.target_value ?? 0;
  const pct =
    typeof goal.progress === "number" && Number.isFinite(goal.progress)
      ? Math.round(goal.progress)
      : target > 0
        ? Math.round((current / target) * 100)
        : 0;
  const createdMs = goal.created ?? nowMs;
  // review 口径与非汇报态一致（GoalDetailPage :157）：JSON 串 {"title","summary"} 拆 headline/summary；
  // 解析失败整串作 title；无 review → summary 兜底；均无 → A.6 顺位 5 只渲染 Day n + 起始行
  const reviewData = parseGoalReview(goal.review);
  const body = reviewData
    ? reviewData.summary?.trim() || null
    : goal.summary?.trim() || null;

  const plans: ReportGoalPlan[] = (goal.trackers ?? [])
    .filter(
      (tracker): tracker is Tracker & { tracker_id: string } =>
        tracker.actor === TrackerActorEnum.USER &&
        typeof tracker.tracker_id === "string" &&
        tracker.tracker_id.length > 0,
    )
    .map((tracker) => {
      const hasUnread = (tracker.unread_count ?? 0) > 0;
      return {
        plan_id: tracker.tracker_id,
        name: tracker.title ?? "",
        status: toReportPlanStatus(tracker, nowMs),
        updated_at: new Date(
          tracker.modified ?? tracker.created ?? nowMs,
        ).toISOString(),
        tracker_count: 1,
        has_unread: hasUnread,
        // record 明细在 plan 层不可得（getGoal 不含）；标题行取 tracker 级最新触发标题，
        // content_json 留空由渲染层落 tracker 描述兜底（normalizeReportSignalContent(null)→null）
        latest_unread: hasUnread
          ? {
              title: tracker.last_trigger_title ?? tracker.title ?? "",
              content_json: null,
            }
          : null,
        trackers: [
          {
            tracker_id: tracker.tracker_id,
            name: tracker.title ?? "",
            description: tracker.description ?? null,
          },
        ],
      };
    });

  return {
    goal_id: goal.id,
    statement: goal.title,
    category: goal.category,
    summary: goal.summary ?? undefined,
    progress: {
      current,
      target,
      unit: goal.value_unit ?? "USD",
      months_left: goal.deadline
        ? Math.max(0, Math.ceil((goal.deadline - nowMs) / MONTH_MS))
        : 0,
      pct: Math.max(0, Math.min(100, pct)),
      fitness_delta: 0,
    },
    our_progress: {
      day: Math.max(1, Math.floor((nowMs - createdMs) / DAY_MS) + 1),
      started_on: toLocalIsoDate(new Date(createdMs)),
      headline: reviewData?.title ?? null,
      body,
    },
    plans,
    // 分数影响暂无现成数据源 → 缺省（GoalFitnessImpact 自身不渲染兜底）
    impact: null,
  };
}

async function fetchGoal(
  trackerApi: TrackerClient<unknown>,
  goalId: string,
): Promise<Goal> {
  const res = await trackerApi.goals.getGoal(goalId, { enrich_trackers: true });
  const goal = res.data?.data;
  if (!goal) {
    throw new Error(`trackerListAdapter: goal ${goalId} response missing data`);
  }
  return goal;
}

/** signal 正文拼装（plan A：records title/description 拼） */
function buildReportContent(
  record: TrackerRecord | undefined,
): string | undefined {
  const content = [record?.title, record?.description, record?.content]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join("\n\n");
  return content.length > 0 ? content : undefined;
}

export const trackerListAdapter: ReportDataAdapter = {
  async fetchSummary(deps) {
    const rows = await listAllUnreadTrackers(deps);
    const group = buildGroup(rows, toLocalIsoDate(), deps);
    // 恒等单点：unread_count = 三块之和
    const unread_count =
      group.goals.length +
      group.trackers.length +
      (group.market_summary ? 1 : 0);

    // 本地 watermark 派生（mock fetchSummary 同形）：当日已进过汇报 → 对照上次快照差集算新到
    const today = toLocalIsoDate();
    const enteredToday = hasEnteredReportToday(today);
    const watermark = readReportSummaryWatermark(deps.ownerId);
    const taskIds = [...group.goals, ...group.trackers].map((t) => t.task_id);
    const new_count =
      enteredToday && watermark?.date === today
        ? taskIds.filter((id) => !watermark.task_ids.includes(id)).length
        : 0;
    writeReportSummaryWatermark(deps.ownerId, taskIds, today);

    return {
      unread_count,
      new_count,
      bar_state: enteredToday && new_count >= 1 ? "new_since" : "unread",
    };
  },

  async fetchGroup(date, deps) {
    const rows = await listAllUnreadTrackers(deps);
    return buildGroup(rows, date, deps);
  },

  async fetchGoalDetail(goalId, { trackerApi }) {
    const goal = await fetchGoal(trackerApi, goalId);
    return mapGoalToReportDetail(goal);
  },

  async fetchAskEdContext(ref, { trackerApi }) {
    if ("trackerId" in ref) {
      const [trackerRes, recordsRes] = await Promise.all([
        trackerApi.trackers.getTracker(ref.trackerId),
        trackerApi.trackers.listTrackerRecords(ref.trackerId, {
          limit: LIST_PAGE_SIZE,
          unread: true,
        }),
      ]);
      const tracker = trackerRes.data?.data;
      const records = normalizePagination<TrackerRecordPagination>(
        recordsRes?.data ?? recordsRes,
      );
      const rows = records.rows ?? [];
      const latest =
        rows.length > 0
          ? rows.reduce((a, b) =>
              (b.created ?? 0) >= (a.created ?? 0) ? b : a,
            )
          : undefined;
      const goalId = latest?.goal_id ?? tracker?.goal_id;
      return {
        signal_id: latest?.id,
        signal_type: latest?.event_type ?? "signal",
        signal_title:
          latest?.title ?? tracker?.last_trigger_title ?? tracker?.title ?? "",
        tracker_id: ref.trackerId,
        goal_id: goalId,
        relation_tier: goalId ? ("goal" as const) : undefined,
        report_content: buildReportContent(latest),
        signal_at: epochMsToIso(latest?.created),
        entry: "report",
        locale: "en",
      };
    }
    if ("goalId" in ref) {
      const goal = await fetchGoal(trackerApi, ref.goalId);
      const detail = mapGoalToReportDetail(goal);
      return {
        goal_id: detail.goal_id,
        signal_title: detail.statement,
        plan_names: detail.plans
          .map((plan) => plan.name.trim())
          .filter(Boolean),
        entry: "report",
        locale: "en",
      };
    }
    return {
      signal_title: "Daily market summary",
      report_date: ref.reportDate,
      entry: "report",
      locale: "en",
    };
  },

  async settleTaskRead(task, deps) {
    if (!deps) {
      throw new Error("trackerListAdapter.settleTaskRead: deps required");
    }
    const trackerIds =
      task.kind === "goal" ? (task.trackerIds ?? []) : [task.taskId];
    if (trackerIds.length === 0) {
      // 评审批 P2：goal 落定无成员时静默成功=假落定（数字已清、records 未标）——
      // 抛错走 useReportReadSettlement 的重试+Track.error 自愈链
      throw new Error(
        `trackerListAdapter.settleTaskRead: goal ${task.taskId} has no tracker ids to settle`,
      );
    }
    await Promise.all(
      trackerIds.map((trackerId) =>
        deps.trackerApi.trackers.updateTracker(trackerId, {
          owner_id: deps.ownerId,
          unread_count: 0,
        }),
      ),
    );
  },

  async settleDailySummaryRead() {
    // no-op：市场总结已读由消费方 deps.markDailySummaryRead（useDailyMarketReadState().markRead）
    // 本地落定；保留方法签名供 mock 期测试与接口稳定性
  },

  // sendFeedback 已撤接口：反馈走 useDetailFeedback 缺省埋点（brief_feedback_submit）

  async sendLike() {
    // like 后端接口未出设计：页面埋点（brief_report_like/undo）已覆盖行为口径，此处 no-op
  },

  async undoLike() {},
};
